// @LTG-ID: contact-sending-error
// @STATUS: BUILT
// @HERMETIC: true
// @DOCS: ORCH-0087 point 4 (3 sub-variants)
// @CHANGED: 2026-05 session_006 (CHG-0083-M-A2)
// @OWNER: web
//
// CHG-0083-M-A2 — SendingError screen.
//
// Shown instead of <Confirmation> when POST /api/contact fails.
// 3 sub-variants per ORCH-0087 point 4:
//
//   "rate_limit" (HTTP 429) — countdown timer ticks down to 0 then enables
//                              the Try-again CTA. Uses Retry-After header
//                              value from the response.
//   "unavailable" (HTTP 503) — Try-again CTA always enabled.
//   "network"     (network error / unexpected status) — Try-again CTA always
//                                                       enabled.
//
// CTAs:
//   "Try again"  — re-fires onRetry() (parent reuses the SAME idempotency
//                  key so backend dedupes if first attempt actually went
//                  through but response was lost).
//   "Start over" — onRestart() clears formData + idempotency key, returns
//                  to step 1.
//
// A.3 #1: v4 source has NO <SendingError> component (submit was pure local
// mock with no API call). This component is an orchestrator extension per
// ORCH-0087 point 4 — building per spec.
//
// ARIA: role="alert" + aria-live="assertive" so screen readers announce
// the error state immediately on mount.

import { useEffect, useState } from "react";

const COPY_VARIANTS = {
    rate_limit: {
        head: "Too many messages right now",
        bodyTemplate: (s) =>
            s > 0
                ? `We're getting too many messages right now. Your message wasn't sent. Try again in ${s} second${s === 1 ? "" : "s"}.`
                : "We're getting too many messages right now. Your message wasn't sent. Try again now.",
    },
    unavailable: {
        head: "Couldn't send right now",
        bodyTemplate: () =>
            "We couldn't send your message — our system is having trouble. Try again in a moment.",
    },
    network: {
        head: "Couldn't reach our server",
        bodyTemplate: () =>
            "Check your connection and try again.",
    },
};

export const SendingError = ({ kind, retryAfter, onRetry, onRestart }) => {
    const safeKind = COPY_VARIANTS[kind] ? kind : "network";
    const initialSeconds =
        safeKind === "rate_limit"
            ? Math.max(0, Math.floor(retryAfter || 0))
            : 0;
    const [secondsLeft, setSecondsLeft] = useState(initialSeconds);

    useEffect(() => {
        if (safeKind !== "rate_limit") return undefined;
        if (secondsLeft <= 0) return undefined;
        const t = setInterval(() => {
            setSecondsLeft((s) => Math.max(0, s - 1));
        }, 1000);
        return () => clearInterval(t);
    }, [safeKind, secondsLeft]);

    const variant = COPY_VARIANTS[safeKind];
    const canRetry = safeKind !== "rate_limit" || secondsLeft === 0;

    return (
        <section
            className="ltgc-step ltgc-step-error"
            data-testid="contact-sending-error"
            data-error-kind={safeKind}
            role="alert"
            aria-live="assertive"
            aria-labelledby="ltgc-sending-error-heading"
        >
            <div className="ltgc-error-icon" aria-hidden="true">
                <svg viewBox="0 0 64 64">
                    <circle
                        cx="32"
                        cy="32"
                        r="28"
                        fill="none"
                        stroke="var(--error-line)"
                        strokeWidth="3"
                    />
                    <line
                        x1="32"
                        y1="18"
                        x2="32"
                        y2="36"
                        stroke="var(--error-line)"
                        strokeWidth="3"
                        strokeLinecap="round"
                    />
                    <circle cx="32" cy="44" r="2.5" fill="var(--error-line)" />
                </svg>
            </div>
            <span className="ltgc-step-greeting ltgc-step-greeting-error">
                Submission paused
            </span>
            <h2
                id="ltgc-sending-error-heading"
                className="ltgc-error-head"
                data-testid="contact-error-head"
            >
                {variant.head}
            </h2>
            <p
                className="ltgc-sub"
                data-testid="contact-error-body"
            >
                {variant.bodyTemplate(secondsLeft)}
            </p>
            {safeKind === "rate_limit" && secondsLeft > 0 && (
                <p
                    className="ltgc-error-countdown"
                    aria-live="polite"
                    data-testid="contact-error-countdown"
                >
                    {secondsLeft}s
                </p>
            )}
            <div className="ltgc-step-nav">
                <button
                    type="button"
                    className="ltgc-btn ltgc-btn-ghost"
                    onClick={onRestart}
                    data-testid="contact-error-restart"
                >
                    ← Start over
                </button>
                <button
                    type="button"
                    className="ltgc-btn ltgc-btn-primary"
                    disabled={!canRetry}
                    onClick={onRetry}
                    data-testid="contact-error-retry"
                >
                    Try again <span className="ltgc-arrow">→</span>
                </button>
            </div>
        </section>
    );
};

export default SendingError;
